import React from "react";

import { observer } from "mobx-react-lite";

import {
  IconChatAttention,
  IconDeleteForever,
  IconDownload,
  IconEdit,
  IconFileAdd,
  IconFileInfo,
  IconFullscreen,
  IconViewer,
} from "@synthesis/shared-icons";
import { getApiBaseUrl } from "@synthesis/shared-services";
import type { FileMetaDto } from "../../api";
import { saveModelIdsAtRedis } from "../../api";
import { LoadingIndicator, type ContextMenuItem } from "@synthesis/shared-ui";
import { logger } from "@synthesis/shared-utils";

import { useUploadProgress } from "../../file-uploader/file-uploader";
import useProjectMeta from "../../hooks/use-project-meta";
import { ExplorerView, StoreState, useStores } from "../../store";
import { DocumentsTypes } from "../../types";
import { ModalReName } from "../explorer/local-components";
import ModalMeta from "../meta-data/modal-meta";
import { useVersions } from "../meta-data/hooks";
import { useMarkUp, useModelGantt, type ContextMenuStateFunctions } from "./hooks";
import {
  GridView,
  ListView,
  ModalConvertReport,
  ModalDeleteFile,
  ModalDeleteSomeFiles,
  ModalIntegrationProject,
  ModalMarkupPreview,
  ModalPhotoPreview,
  ModalPreview,
  TableView,
} from "./local-components";

type FilesProps = {
  id?: string;
  disabledRight?: boolean;
  isIntegration?: boolean;
  openPdfModal?: (file: FileMetaDto) => void;
};

type ProjectMetaItem = {
  key: string;
  value: Array<{ id: string; name?: string }> | string;
};

type FileMetaContainerProps = {
  View: React.ComponentType<Record<string, unknown>>;
  innerMetaFile: unknown;
  ganttModels: string[];
  getMeta: (modelId: string) => ProjectMetaItem[] | undefined;
  data: FileMetaDto;
  state: ContextMenuItem[];
  [key: string]: unknown;
};

let timer: ReturnType<typeof setTimeout> | undefined;

const FileVariants = {
  [ExplorerView.Grid]: GridView,
  [ExplorerView.List]: ListView,
};

const FileMetaContainer = ({ View, innerMetaFile, ganttModels, getMeta, data, state, ...rest }: FileMetaContainerProps) => {
  const [metaFile, setMetaFile] = React.useState<ProjectMetaItem[]>([]);

  React.useEffect(() => {
    (async () => {
      const meta = await getMeta(data.id);
      setMetaFile(Array.isArray(meta) ? meta : []);
    })();
  }, [data.id, getMeta]);

  React.useEffect(() => {
    (async () => {
      const meta = await getMeta(data.id);
      setMetaFile(Array.isArray(meta) ? meta : []);
    })();
  }, [innerMetaFile, data.id, getMeta]);

  const isEngineering = Boolean(
    metaFile.find((meta) => meta.key === "engineering" && Array.isArray(meta.value) && meta.value.find((item) => item.id === data.id)),
  );
  const isModel = Boolean(
    metaFile.find((meta) => meta.key === "models" && Array.isArray(meta.value) && meta.value.find((item) => item.id === data.id)),
  );

  return (
    <View
      key={data.id}
      isUsingInGantt={ganttModels.includes(data.id)}
      isEngineering={isEngineering}
      isModel={isModel}
      data={data}
      state={state}
      {...rest}
    />
  );
};

type FileViewProps = {
  id?: string;
  innerMetaFile: ProjectMetaItem[];
  ganttModels: string[];
  getMeta: FileMetaContainerProps["getMeta"];
  state: ContextMenuItem[];
  stateFunctions: ContextMenuStateFunctions;
  onClickModel: () => void;
  onClickFile: (e: React.MouseEvent, imgSrc: string | null, data: FileMetaDto) => void;
  setSelectedFile: (file: FileMetaDto) => void;
  disabledRight?: boolean;
};

const FileView = observer(({ innerMetaFile, ganttModels, getMeta, state, stateFunctions, ...rest }: FileViewProps) => {
  const { generalStore, fileStore, directoryStore } = useStores();

  const View = React.useMemo(() => {
    if (generalStore.view === ExplorerView.Table) return null;
    return FileVariants[generalStore.view as keyof typeof FileVariants];
  }, [generalStore.view]);

  const visibleFiles = fileStore.visibleFiles;

  if (generalStore.view === ExplorerView.Table) {
    return (
      <TableView
        onClickModel={rest.onClickModel}
        setSelectedFile={rest.setSelectedFile}
        disabledRight={rest.disabledRight ?? false}
        stateFunctions={stateFunctions}
      />
    );
  }

  if (generalStore.state === StoreState.Loading || generalStore.state === StoreState.Initial) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <LoadingIndicator />
      </div>
    );
  }

  if (visibleFiles.length > 0 && View) {
    return (
      <>
        {visibleFiles.map((data, index) => (
          <FileMetaContainer
            key={index}
            ganttModels={ganttModels as string[]}
            View={View.File as React.ComponentType<Record<string, unknown>>}
            innerMetaFile={innerMetaFile}
            getMeta={getMeta as FileMetaContainerProps["getMeta"]}
            data={data}
            state={state as ContextMenuItem[]}
            stateFunctions={stateFunctions}
            {...rest}
          />
        ))}
      </>
    );
  }

  if (visibleFiles.length === 0 && directoryStore.directories.length === 0) {
    return (
      <p data-cy="files-p-filesystem" className="text-type-minor text-center text-xl">
        Папка пустая
      </p>
    );
  }

  return null;
});

const Files = observer<FilesProps>(({ id, disabledRight, isIntegration, openPdfModal }) => {
  const { generalStore, fileStore } = useStores();
  const ref = React.useRef<HTMLInputElement | null>(null);

  const [isOpenMeta, setIsOpenMeta] = React.useState(false);
  const [isOpenDeleteFile, setIsOpenDeleteFile] = React.useState(false);
  const [isOpenDeleteSomeFiles, setIsOpenDeleteSomeFiles] = React.useState(false);

  const [isPreviewOpen, setIsPeviewOpen] = React.useState(false);
  const [isMarkupOpen, setIsMarkupOpen] = React.useState(false);

  const [isOpenReName, setIsOpenReName] = React.useState(false);
  const [isConvertOpen, setIsConvertOpen] = React.useState(false);

  const [isOpenPreview, setIsOpenPreview] = React.useState(false);

  const [selectedFile, setSelectedFile] = React.useState<FileMetaDto | null>(null);

  const [isModalIntegration, setIsModalIntegration] = React.useState({
    isOpen: false,
    rename: false,
    delete: false,
    updateVersion: false,
  });

  const { projectMeta, getMeta } = useProjectMeta(generalStore.projectId);

  const { ganttModels, patchModelIdFromGantt } = useModelGantt(generalStore.projectId ?? "");

  const [metaFile, setMetaFile] = React.useState<ProjectMetaItem[]>([]);

  const { getVersions } = useVersions();

  const { markupsCount } = useMarkUp(selectedFile?.id);

  const { versionUpdate } = useUploadProgress();

  const handleClickReName = () => {
    if (isIntegration) {
      setIsModalIntegration((prevState) => ({
        ...prevState,
        rename: true,
        isOpen: true,
      }));
    } else {
      setIsOpenReName(true);
    }
  };

  const fetchMetaFile = React.useCallback(async () => {
    if (!selectedFile) return;
    const meta = await getMeta(selectedFile.id);

    setMetaFile(Array.isArray(meta) ? meta : []);
  }, [selectedFile, getMeta]);

  React.useEffect(() => {
    if (selectedFile) {
      fetchMetaFile();
    }
  }, [selectedFile, projectMeta, fetchMetaFile]);

  const handleClickAddNewVersion = () => {
    if (isIntegration) {
      setIsModalIntegration((prevState) => ({
        ...prevState,
        updateVersion: true,
        isOpen: true,
      }));
    } else {
      ref.current?.click();
    }
  };

  const handleDeleteFile = () => {
    if (isIntegration) {
      setIsModalIntegration((prevState) => ({
        ...prevState,
        delete: true,
        isOpen: true,
      }));
    } else if (generalStore.checkedFiles.size > 1) {
      setIsOpenDeleteSomeFiles(true);
    } else {
      setIsOpenDeleteFile(true);
    }
  };

  const handleClickConvert = () => {
    setIsConvertOpen(true);
  };

  const onUpdateVersion = (newFileId: string) => {
    if (selectedFile?.id) {
      patchModelIdFromGantt(selectedFile.id, newFileId);
    }
  };

  const openViewer = (file?: FileMetaDto) => {
    const modelsId: string[] = [];
    if (!file) {
      const models = Array.from(generalStore.checkedFiles.values())
        .map((e) => e.id)
        .join(",");
      modelsId.push(...models.split(","));
    }
    if (file) {
      modelsId.push(file.id);
    }
    void (async () => {
      try {
        const generateModelId = (await saveModelIdsAtRedis({ body: { modelIds: modelsId } })).data;
        const url = `${getApiBaseUrl()}/viewer/#/${generalStore.projectId}?models=${generateModelId}`;
        window.open(url, "_blank")?.focus();
      } catch (e) {
        logger.error("open viewer failed", e);
      }
    })();
  };

  const contextMenuContentModel: ContextMenuItem[] = [
    {
      icon:
        generalStore.checkedFiles.size <= 1 ? (
          <IconFullscreen className="text-neutrals-60 size-5" />
        ) : (
          <IconViewer className="text-neutrals-60 size-5" />
        ),
      label: generalStore.checkedFiles.size <= 1 ? "Открыть отдельно" : "Открыть сборку",
      onClick: generalStore.checkedFiles.size <= 1 ? () => openViewer(selectedFile ?? undefined) : () => openViewer(),
    },
    ...(generalStore.checkedFiles.size <= 1
      ? [
          {
            icon: <IconEdit className="text-neutrals-60 size-5" />,
            label: "Переименовать",
            disabled: disabledRight,
            onClick: handleClickReName,
            // ...(auth.currentUser.permissionsBlacklist?.includes('RENAME_FILE_FILESYSTEM') ? { disabled: true } : {}),
          },
        ]
      : []),
    { separator: true },
    ...(generalStore.checkedFiles.size <= 1
      ? [
          {
            icon: <IconDownload className="text-neutrals-60 size-5" />,
            label: "Скачать",
            onClick: () => {
              if (!selectedFile) return;
              fileStore.downloadFile({
                id: selectedFile.id,
                baseName: selectedFile.baseName || "Безымянный",
                ext: "ifc",
              });
            },
          },
        ]
      : [
          {
            icon: <IconDownload className="text-neutrals-60 size-5" />,
            label: "Скачать выбранные архивом",
            onClick: () => {
              if (!selectedFile) return;
              fileStore.downloadArchiveFile({
                id: selectedFile.id,
                baseName: selectedFile.baseName || "Безымянный",
                ext: selectedFile.ext ?? "",
              });
            },
          },
        ]),

    ...(generalStore.checkedFiles.size <= 1
      ? [
          { separator: true },
          {
            icon: <IconChatAttention className="text-neutrals-60 size-5" />,
            label: "Замечания",
            onClick: () => setIsMarkupOpen(true),
          },
          { separator: true },
          {
            icon: <IconFileAdd className="text-neutrals-60 size-5" />,
            label: "Загрузить версию",
            disabled: disabledRight,
            onClick: handleClickAddNewVersion,
          },
          {
            icon: <IconFileInfo className="text-neutrals-60 size-5" />,
            label: "Свойства и версии",
            onClick: () => {
              if (!selectedFile) return;
              getVersions(selectedFile.id);
              setIsOpenMeta(true);
            },
          },
        ]
      : []),
    ...(generalStore.checkedFiles.size <= 1
      ? [
          {
            label: "Отчёт о конвертации",
            onClick: handleClickConvert,
            // ...(auth.currentUser.permissionsBlacklist?.includes('RENAME_FILE_FILESYSTEM') ? { disabled: true } : {}),
          },
        ]
      : []),
    { separator: true },
    {
      icon: <IconDeleteForever className="size-5" />,
      label: generalStore.checkedFiles.size > 1 ? "Удалить выбранные файлы" : "Удалить файл",
      disabled: disabledRight,
      onClick: handleDeleteFile,
      activeRed: true,
    },
  ];

  const contextMenuContent = contextMenuContentModel;

  const contextMenuContentDocs: ContextMenuItem[] = [
    ...(generalStore.checkedFiles.size <= 1
      ? [
          {
            icon: <IconFullscreen className="text-neutrals-60 size-5" />,
            label: "Открыть",
            onClick: () => openDoc(selectedFile ?? undefined),
          },
          {
            icon: <IconEdit className="text-neutrals-60 size-5" />,
            label: "Переименовать",
            disabled: disabledRight,
            onClick: handleClickReName,
          },
        ]
      : []),
    { separator: true },
    ...(generalStore.checkedFiles.size <= 1
      ? [
          {
            icon: <IconDownload className="text-neutrals-60 size-5" />,
            label: "Скачать",
            onClick: () => {
              if (!selectedFile) return;
              fileStore.downloadFile({
                id: selectedFile.id,
                baseName: selectedFile.baseName || "Безымянный",
                ext: selectedFile.ext ?? "",
              });
            },
          },
        ]
      : [
          {
            icon: <IconDownload className="text-neutrals-60 size-5" />,
            label: "Скачать выбранные архивом",
            onClick: () => {
              if (!selectedFile) return;
              fileStore.downloadArchiveFile({
                id: selectedFile.id,
                baseName: selectedFile.baseName || "Безымянный",
                ext: selectedFile.ext ?? "",
              });
            },
          },
        ]),
    { separator: true },
    {
      icon: <IconDeleteForever className="size-5" />,
      label: generalStore.checkedFiles.size > 1 ? "Удалить выбранные файлы" : "Удалить файл",
      disabled: disabledRight,
      onClick: handleDeleteFile,
      activeRed: true,
    },
  ];

  const contextMenuContentFile: ContextMenuItem[] = [
    {
      label: generalStore.checkedFiles.size > 1 ? "Скачать выбранные архивом" : "Скачать архив",
      onClick: () => {
        if (!selectedFile) return;
        fileStore.downloadArchiveFile({
          id: selectedFile.id,
          baseName: selectedFile.baseName || "Безымянный",
          ext: selectedFile.ext ?? "",
        });
      },
    },
    ...(generalStore.checkedFiles.size <= 1
      ? [
          {
            label: "Скачать файл",
            onClick: () => {
              if (!selectedFile) return;
              fileStore.downloadFile({
                id: selectedFile.id,
                baseName: selectedFile.baseName || "Безымянный",
                ext: selectedFile.ext ?? "",
              });
            },
            dataCy: "files-menuItem-projectDocks",
          } as ContextMenuItem,
          {
            label: "Переименовать файл",
            onClick: handleClickReName,
            dataCy: "files-menuItem-projectDocks",
          } as ContextMenuItem,
          {
            label: "Загрузить новую версию",
            onClick: handleClickAddNewVersion,
            dataCy: "files-menuItem-projectDocks",
          } as ContextMenuItem,
          {
            label: "Свойства",
            onClick: () => {
              if (!selectedFile) return;
              getVersions(selectedFile.id);
              setIsOpenMeta(true);
            },
            dataCy: "files-menuItem-projectDocks",
          } as ContextMenuItem,
        ]
      : []),
    { separator: true },
    {
      label: generalStore.checkedFiles.size > 1 ? "Удалить выбранные файлы" : "Удалить файл",
      onClick: generalStore.checkedFiles.size > 1 ? () => setIsOpenDeleteSomeFiles(true) : () => setIsOpenDeleteFile(true),
      dataCy: "files-menuItem-projectDocks",
      activeRed: true,
    } as ContextMenuItem,
  ];

  const uploadFileRequest = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !selectedFile) return;
    try {
      generalStore.setState("loading");
      versionUpdate(selectedFile, file);

      generalStore.setState("success");
      event.target.value = "";
    } catch (e) {
      logger.error("error at upload file >>>", e);
    }
  };

  // eslint-disable-next-line unicorn/consistent-function-scoping
  const onClickModel = () => {};

  const closeModalReName = () => {
    setIsOpenReName(false);
    setSelectedFile(null);
  };

  const closeModalConvert = () => {
    setIsConvertOpen(false);
    setSelectedFile(null);
  };

  const openModalReName = () => {
    setIsOpenReName(true);
  };

  const closeModalMeta = () => {
    setIsOpenMeta(false);
    setSelectedFile(null);
  };

  const openModalMeta = () => {
    setIsOpenMeta(true);
  };

  const closeModalDeleteFile = () => {
    setIsOpenDeleteFile(false);
    setIsOpenDeleteSomeFiles(false);
    setSelectedFile(null);
  };

  const openModalDeleteFile = () => {
    setIsOpenDeleteFile(true);
  };

  const closeModalPhotoPreview = () => {
    setIsOpenPreview(false);
    setSelectedFile(null);
  };

  const openModalPhotoPreview = () => {
    setIsOpenPreview(true);
  };

  const onClickFile = (e: React.MouseEvent, imgSrc: string | null, data: FileMetaDto) => {
    clearTimeout(timer);
    if (e.detail === 1) {
      timer = setTimeout(() => imgSrc && setIsOpenPreview(true), 200);
    } else if (e.detail === 2) {
      if (generalStore.activeTab === "docs" && data.ext?.toLowerCase() === "pdf" && openPdfModal) {
        openPdfModal(data);
      } else {
        fileStore.downloadFile({
          id: data.id,
          baseName: data.baseName || "Безымянный",
          ext: data.ext ?? "",
        });
      }
    }
    setSelectedFile(data);
  };
  const openDoc = (file?: FileMetaDto) => {
    const target = file ?? selectedFile;
    if (!target) {
      return;
    }

    if (target.ext?.toLowerCase() === "pdf" && openPdfModal) {
      openPdfModal(target);
      return;
    }

    fileStore.downloadFile({
      id: target.id,
      baseName: target.baseName || "Безымянный",
      ext: target.ext ?? "",
    });
  };

  const stateFunctions = {
    handleClickReName,
    fetchMetaFile,
    handleClickConvert,
    setIsPeviewOpen,
    setIsMarkupOpen,
    handleClickAddNewVersion,
    setIsOpenMeta,
    setIsOpenDeleteSomeFiles,
    setIsOpenDeleteFile,
    openViewer,
    handleDeleteFile,
    openDoc,
  };
  return (
    <>
      <FileView
        id={id}
        innerMetaFile={metaFile}
        ganttModels={ganttModels}
        getMeta={getMeta}
        onClickModel={onClickModel}
        onClickFile={onClickFile}
        setSelectedFile={(file) => setSelectedFile(file)}
        state={
          generalStore.activeTab === "docs"
            ? contextMenuContentDocs
            : selectedFile?.type === DocumentsTypes.model
              ? contextMenuContent
              : contextMenuContentFile
        }
        stateFunctions={stateFunctions}
        disabledRight={disabledRight}
      />
      {isOpenDeleteFile && (
        <ModalDeleteFile
          deleteId={selectedFile?.id}
          isOpen={isOpenDeleteFile}
          setIsOpen={setIsOpenDeleteFile}
          openModal={openModalDeleteFile}
          closeModal={closeModalDeleteFile}
        />
      )}
      {isOpenDeleteSomeFiles && (
        <ModalDeleteSomeFiles
          isOpen={isOpenDeleteSomeFiles}
          setIsOpen={setIsOpenDeleteSomeFiles}
          openModal={openModalDeleteFile}
          closeModal={closeModalDeleteFile}
        />
      )}

      {isOpenPreview && selectedFile && (
        <ModalPhotoPreview
          id={selectedFile.id}
          isOpen={isOpenPreview}
          setIsOpen={setIsOpenPreview}
          openModal={openModalPhotoPreview}
          closeModal={closeModalPhotoPreview}
        />
      )}
      {selectedFile && (
        <ModalPreview
          modelId={selectedFile.id}
          isOpen={isPreviewOpen}
          setIsOpen={setIsPeviewOpen}
          openModal={() => setIsPeviewOpen(true)}
          closeModal={() => setIsPeviewOpen(false)}
        />
      )}
      {selectedFile && (
        <ModalMarkupPreview
          sourceId={selectedFile.sourceId}
          modelId={selectedFile.id}
          markupsCount={markupsCount ?? undefined}
          isOpen={isMarkupOpen}
          setIsOpen={setIsMarkupOpen}
          openModal={() => setIsMarkupOpen(true)}
          closeModal={() => setIsMarkupOpen(false)}
          file={selectedFile}
        />
      )}
      {selectedFile && isOpenMeta && (
        <ModalMeta
          onUpdateVersion={onUpdateVersion}
          info={selectedFile}
          selectedFile={selectedFile}
          onClickModel={onClickModel}
          isOpen={isOpenMeta}
          setIsOpen={setIsOpenMeta}
          openModal={openModalMeta}
          closeModal={closeModalMeta}
          stateFunctions={stateFunctions}
        />
      )}

      {isOpenReName && selectedFile && (
        <ModalReName
          name={selectedFile.baseName}
          id={selectedFile.id}
          type={selectedFile.type}
          isOpen={isOpenReName}
          setIsOpen={setIsOpenReName}
          openModal={openModalReName}
          closeModal={closeModalReName}
        />
      )}

      {isConvertOpen && selectedFile && (
        <ModalConvertReport
          title={selectedFile.baseName}
          id={selectedFile.id}
          isOpen={isConvertOpen}
          setIsOpen={setIsConvertOpen}
          openModal={() => setIsConvertOpen(true)}
          closeModal={closeModalConvert}
        />
      )}

      <ModalIntegrationProject
        isOpen={isModalIntegration.isOpen}
        setIsOpen={(newState) => setIsModalIntegration((prevState) => ({ ...prevState, isOpen: newState }))}
        openModal={() => setIsModalIntegration((prevState) => ({ ...prevState, isOpen: true }))}
        closeModal={() =>
          setIsModalIntegration(() => ({
            rename: false,
            isOpen: false,
            updateVersion: false,
            delete: false,
          }))
        }
        isModalIntegration={isModalIntegration}
        disabledRight={disabledRight}
        setIsOpenRename={setIsOpenReName}
        setIsOpenDeleteFile={setIsOpenDeleteFile}
        setIsModalIntegration={setIsModalIntegration}
        uploadFileRequest={() => ref.current?.click()}
      />

      {/* input for upload new version file */}
      {selectedFile?.id && (
        <input data-cy="files-input-projectDocks" type="file" id="file" className="hidden" ref={ref} onChange={uploadFileRequest} />
      )}
    </>
  );
});

export default Files;
